import { defineStore } from 'pinia';
import { appendHeader, sendRedirect } from "h3";
import type { City } from "~/entities/city";

type SummaryShort = {
    id: string,
    title: string,
    status: number,
    visibility?: number,
    professions?: { id: number, title: string }[],
}

type Client = {
    accessToken: string | null,
    id: string | null,
    name: string | null,
    surname: string | null,
    dateBirth: number | null,
    phone: string | null,
    photo: string | null,
    email: string | null,
    city: City | null,
    isNotificationChannelConnected: boolean,
    isSummaryCreated?: boolean,
    isAdmin: boolean,
    summaryId?: string | null,
    summaries: SummaryShort[],
    companies: Company[],
    activeCompany: Company | null,
    ownedProfileImageUrls?: Record<string, string>,
    role: number,
}

type Company = {
    id: string,
    name: string,
    img: string,
    isVacancyCreated: boolean,
    vacanciesCount: number,
    type: boolean
};

function getCookieDomainAttribute(domain: unknown): string {
    if (typeof domain !== 'string') {
        return '';
    }

    let hostname = domain.trim().replace(/^\./, '');

    // Check for IPv6 loopback before stripping port
    const isIPv6Loopback = hostname === '::1' || hostname === '[::1]' || hostname.startsWith('[::1]:');

    // Strip port suffix
    hostname = hostname.replace(/:\d+$/, '');

    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || isIPv6Loopback;

    return hostname === '' || isLocalhost ? '' : `;domain=.${hostname}`;
}

function useCompanyCookie() {
    const config = useRuntimeConfig();
    const domain = getCookieDomainAttribute(config.public.domain)
        .replace(/^;domain=/, '') || undefined;

    return useCookie<string | null>('company', {
        default: () => null,
        maxAge: 60 * 24 * 60 * 60,
        path: '/',
        sameSite: 'lax',
        domain,
    });
}

export const useClient = defineStore('client', {
    state: (): Client => ({
        accessToken: null,
        id: null,
        name: null,
        surname: null,
        dateBirth: null,
        phone: null,
        photo: null,
        email: null,
        city: null,
        isNotificationChannelConnected: false,
        isSummaryCreated: false,
        isAdmin: false,
        summaryId: null,
        summaries: [],
        companies: [],
        activeCompany: null,
        ownedProfileImageUrls: {},
        role: 0,
    } as Client),

    actions: {
        normalizeCity(input: unknown): City | null {
            const defaultCities: Record<string, City> = {
                novosibirsk: {
                    id: 2,
                    title: 'Новосибирск',
                    titleIn: 'в Новосибирске',
                    alias: 'novosibirsk',
                },
                tomsk: {
                    id: 4,
                    title: 'Томск',
                    titleIn: 'в Томске',
                    alias: 'tomsk',
                },
            };

            let city = (input && typeof input === 'object' && 'city' in input)
              ? (input as { city?: unknown }).city
              : input;

            if (typeof city === 'string') {
                try {
                    city = JSON.parse(city);
                } catch {
                    city = defaultCities[city] ?? null;
                }
            }

            if (!city || typeof city !== 'object') {
                return null;
            }

            const typedCity = city as Partial<City> & { id?: unknown; title?: unknown; alias?: unknown };
            if (typeof typedCity.id !== 'number' || typeof typedCity.title !== 'string' || typeof typedCity.alias !== 'string') {
                return null;
            }

            return {
                id: typedCity.id,
                title: typedCity.title,
                alias: typedCity.alias,
                titleIn: typeof typedCity.titleIn === 'string' ? typedCity.titleIn : undefined,
            };
        },
        async defineCity () {
            const defaultCity: City = this.normalizeCity('novosibirsk') ?? {
                id: 2,
                title: 'Новосибирск',
                titleIn: 'в Новосибирске',
                alias: 'novosibirsk',
            };
            const city = useCookie('city');
            let resolvedCity = this.normalizeCity(city?.value);

            if (resolvedCity === null) {
                const event = useRequestEvent();
                const runtimeConfig = useRuntimeConfig();
                try {
                    const response = await $fetch('define-city', {
                        baseURL: import.meta.server
                            ? runtimeConfig.apiProxyTarget
                            : (runtimeConfig.public.apiBase ?? '/api/'),
                        headers: import.meta.server ? useRequestHeaders(['cookie', 'User-Agent', 'X-Forwarded-For', 'X-Real-IP']) : undefined,
                        async onResponse({ response }) {
                            const setCookie = response.headers.get('set-cookie');
                            if (setCookie !== null && event !== undefined) {
                                appendHeader(event, 'set-cookie', setCookie);
                            }
                        }
                    });
                    resolvedCity = this.normalizeCity(response);
                } catch {
                    resolvedCity = null;
                }
            }

            this.city = resolvedCity ?? defaultCity;
            return true;
        },
        async signIn (username: string, code: string): Promise<any> {
            const response = await mainFetch('login', {
                method: 'POST',
                body: { username, code }
            });

            const data = response.data.value;
            if (!response.error.value) {
                this.setAccountData({
                    accessToken: data.accessToken,
                    id: data.client.id,
                    name: data.client.name,
                    surname: data.client.surname,
                    dateBirth: data.client.dateBirth ?? null,
                    phone: data.client.phone,
                    photo: data.client.photo,
                    email: data.client.email,
                    city: data.client.city,
                    isNotificationChannelConnected: data.client.isNotificationChannelConnected,
                    isSummaryCreated: data.client.isSummaryCreated ?? false,
                    isAdmin: data.client.isAdmin,
                    summaryId: data.client.summaryId ?? null,
                    summaries: data.client.summaries ?? [],
                    companies: data.client.companies,
                    activeCompany: data.company ?? null,
                    role: data.client.role,
                });
            }

            return response;
        },
        async refreshToken (forceClientData = false) {
            const event = useRequestEvent();
            const nuxtApp = useNuxtApp();
            const getClientData = forceClientData || this.emptyClientData;

            const response = await mainFetch('refresh-token', {
                method: 'POST',
                body: { getClientData, getCompanyData: false },
                onResponse({ response }) {
                    const cookieList = typeof response.headers.getSetCookie === 'function'
                      ? response.headers.getSetCookie()
                      : [];
                    if (cookieList.length && event !== undefined) {
                        for (const cookie of cookieList) {
                            appendHeader(event, 'set-cookie', cookie);
                        }
                    }
                }
            });

            if (response.error.value) {
                return Promise.resolve();
            }

            const data = response.data.value;
            if (data.accessToken !== undefined) {
                if (getClientData) {
                    nuxtApp.runWithContext(() => this.setAccountData({
                        accessToken: data.accessToken,
                        id: data.client.id,
                        name: data.client.name,
                        surname: data.client.surname,
                        dateBirth: data.client.dateBirth ?? null,
                        phone: data.client.phone,
                        photo: data.client.photo,
                        email: data.client.email,
                        city: data.client.city,
                        isNotificationChannelConnected: data.client.isNotificationChannelConnected,
                        isSummaryCreated: data.client.isSummaryCreated ?? false,
                        isAdmin: data.client.isAdmin,
                        summaryId: data.client.summaryId ?? null,
                        summaries: data.client.summaries ?? [],
                        companies: data.client.companies,
                        activeCompany: data.company,
                        role: data.client.role,
                    }));
                } else {
                    this.accessToken = data.accessToken;
                }
            }

            await nextTick();
        },
        async signOut (redirectTo: string | null = '/') {
            await mainFetch('logout', {
                method: 'POST'
            }).finally(async () => {
                this.clearOwnedProfileImageUrls();
                this.accessToken = null;
                this.id = null;
                this.name = null;
                this.surname = null;
                this.phone = null;
                this.photo = null;
                this.email = null;
                this.city = null;
                this.isNotificationChannelConnected = false;
                this.dateBirth = null;
                this.isAdmin = false;
                this.summaries = [];
                this.companies = [];
                this.activeCompany = null;
                this.role = 0;

                if (redirectTo === null) {
                    return;
                }

                if (import.meta.server) {
                    const requestEvent = useRequestEvent();

                    if (requestEvent !== undefined) {
                        await sendRedirect(requestEvent, redirectTo);
                    }
                } else {
                    const router = useRouter();

                    await router.push(redirectTo);
                }
            });
        },
        loginCompany (company: Company) {
            const companyCookie = useCompanyCookie();

            if (this.activeCompany !== null && this.activeCompany!.id === company.id) {
                // Keep the server-side context in sync even when the UI
                // selects the company that is already active in Pinia.
                companyCookie.value = company.id;
                return;
            }

            companyCookie.value = company.id;
            this.activeCompany = company;
        },
        logoutCompany () {
            if (this.getActiveCompany === null) {
                return;
            }
            const companyCookie = useCompanyCookie();
            companyCookie.value = null;
            this.activeCompany = null;
        },
        replaceOwnedProfileImage (key: string, image: string | null | undefined) {
            const previous = this.ownedProfileImageUrls?.[key];
            if (previous && previous !== image && previous.startsWith('blob:') && import.meta.client) {
                URL.revokeObjectURL(previous);
            }

            if (image?.startsWith('blob:')) {
                this.ownedProfileImageUrls ??= {};
                this.ownedProfileImageUrls[key] = image;
            } else if (this.ownedProfileImageUrls !== undefined) {
                delete this.ownedProfileImageUrls[key];
            }
        },
        clearOwnedProfileImageUrls () {
            for (const key of Object.keys(this.ownedProfileImageUrls ?? {})) {
                this.replaceOwnedProfileImage(key, null);
            }
        },
        setAccountData (data: Client) {
            const hasPhoto = Object.prototype.hasOwnProperty.call(data, 'photo');
            if (hasPhoto) {
                this.replaceOwnedProfileImage('client', data.photo);
            }

            if (Array.isArray(data.companies)) {
                const companyIds = new Set(data.companies.map(company => `company:${company.id}`));
                for (const key of Object.keys(this.ownedProfileImageUrls ?? {})) {
                    if (key.startsWith('company:') && !companyIds.has(key)) {
                        this.replaceOwnedProfileImage(key, null);
                    }
                }

                for (const company of data.companies) {
                    if (Object.prototype.hasOwnProperty.call(company, 'img')) {
                        this.replaceOwnedProfileImage(`company:${company.id}`, company.img);
                    }
                }
            }

            this.accessToken = data.accessToken;
            this.id = data.id;
            this.name = data.name;
            this.surname = data.surname;
            this.dateBirth = data.dateBirth ?? null;
            this.phone = data.phone;
            if (hasPhoto) {
                this.photo = data.photo;
            }
            this.email = data.email;
            this.city = data.city;
            this.isNotificationChannelConnected = data.isNotificationChannelConnected;
            this.isSummaryCreated = data.isSummaryCreated ?? false;
            this.isAdmin = data.isAdmin;
            this.summaryId = data.summaryId ?? null;
            this.summaries = data.summaries ?? [];
            this.companies = data.companies ?? [];

            const companyCookie = useCompanyCookie();
            const selectedCompany = data.activeCompany
                ?? this.companies.find((company) => String(company.id) === String(companyCookie.value))
                ?? null;

            this.activeCompany = selectedCompany;
            if (selectedCompany !== null) {
                companyCookie.value = selectedCompany.id;
            }
            this.role = data.role??0;
        }
    },

    getters: {
        isAuthenticated(): boolean {
            return this.id !== null && this.accessToken !== null;
        },
        emptyClientData(): boolean {
            return this.name === null;
        },
        isCompanies(): boolean {
            return this.companies.length > 0;
        },
        getClientId(): string | null {
            return this.id;
        },
        getCityId(): number | null {
            return this.city?.id ?? null;
        },
        getCityTitle(): string | null {
            return this.city?.title ?? null;
        },
        getActiveCompany(): Company | null {
            return this.activeCompany;
        },
        getClientEntity(): Client {
            return {
                id: this.id,
                name: this.name,
                surname: this.surname,
                email: this.email
            } as Client;
        },
        getCompanyProfileAlias(): string | null {
            if (this.getActiveCompany === null) {
                return null;
            }

            return `/company/${this.getActiveCompany.id}/vacancies`;
        }
    }
})
